import { MelodyLib } from '@tunasong/music-lib'
import type { Melody, MelodyEntityNote } from '@tunasong/schemas'
import { useRef } from 'react'
import type { FC, KeyboardEvent, MouseEvent, PointerEvent } from 'react'
import { applyMelodyDrag } from './melody-drag.js'
import type { MelodyDrag, MelodyDragKind } from './melody-drag.js'
import styles from './melody-roll.module.css'
import {
  beatX,
  isInKey,
  isTonic,
  pointToCell,
  rollBeats,
  rollPitchRange,
  rowCount,
  rowHeight,
  rowY,
} from './melody-roll-geometry.js'
import type { RollRange } from './melody-roll-geometry.js'

/**
 * The melody piano roll.
 *
 * Every mutation goes through a pure `MelodyLib` edit op and comes back out through `onChange`,
 * so the §2 invariants - grid-aligned onsets, ordered, non-overlapping, offsets bounded to half a
 * slot - are enforced in one tested place rather than in pointer handlers. The component owns
 * nothing but the drawing.
 *
 * A note is drawn at `onset + offsetMs / beatMs` and anchored at `onset`: the offset moves where
 * the bar SITS, never which slot it belongs to. That is the whole visual claim of this entity -
 * you can see the feel against the gridline.
 *
 * Without `onChange` the roll is read-only: no insert surface, no keyboard, no handles. That is
 * the mode the block render uses.
 *
 * Joni owns the visual design; v1 ships a functional default on design tokens.
 *
 * Spec: docs/superpowers/specs/2026-09-08-melody-plugin-design.md §4.1
 */

export interface MelodyRollProps {
  melody: Melody
  /** Omit for a read-only roll. */
  onChange?: (melody: Melody) => void
  /** Draw the notes flat, with `offsetMs` treated as zero. */
  quantized?: boolean
  /** ms from the start of playback, or null/undefined when stopped. */
  playheadMs?: number | null
  /** The insert cursor, in beats. */
  cursorBeats?: number
  /** Called after a click-insert so the cursor follows the note that was just placed. */
  onCursorChange?: (beats: number) => void
  selectedIndex?: number | null
  onSelect?: (index: number | null) => void
  /** Render at block height rather than editor height. */
  compact?: boolean
  /**
   * False for a picture of a melody, such as the phrase picker's thumbnail (Phase 2 plan M5 step 5.2):
   * notes are not buttons or tab stops, so an `aria-hidden` wrapper hides nothing focusable.
   */
  interactive?: boolean
  className?: string
}

/** Bar height as a fraction of its pitch row - a gap top and bottom keeps neighbours legible. */
const BAR_FILL = 0.7

/** Velocity as opacity, floored so a quiet note is never invisible. */
const noteOpacity = (velocity: number): number => 0.35 + 0.65 * velocity

/**
 * Where the bar is DRAWN, in beats.
 *
 * The swing displacement comes from `MelodyLib.swingDisplacementMs` - the same helper
 * `scheduleMelody` uses - rather than from a second copy of the arithmetic here. The roll is the
 * surface a musician edits the feel on, so a picture that disagreed with the sound would be
 * misreporting the one thing this editor exists for; two copies of the rule is how that
 * disagreement gets in.
 *
 * `quantized` zeroes the swing as well as the offset, matching the toggle's meaning in playback:
 * both are feel.
 */
const drawnOnset = (note: MelodyEntityNote, melody: Melody, quantized: boolean): number => {
  if (quantized) {
    return note.onset
  }
  const beatMs = MelodyLib.beatMs(melody.tempo.bpm)
  const swing = MelodyLib.swingDisplacementMs(note.onset, melody.feel?.swing ?? 0, beatMs)
  return note.onset + (swing + note.offsetMs) / beatMs
}

/**
 * A plain union rather than an `as const` array: nothing iterates the kinds at runtime, so an
 * array would be a value that only ever gets used as a type.
 */
type RowKind = 'tonic' | 'in-key' | 'out-of-key'

const rowKind = (midi: number, melody: Melody): RowKind => {
  if (isTonic(midi, melody.key)) {
    return 'tonic'
  }
  return isInKey(midi, melody.key) ? 'in-key' : 'out-of-key'
}

const ROW_CLASS: Record<RowKind, string> = {
  tonic: styles.tonicRow,
  'in-key': styles.inKeyRow,
  'out-of-key': styles.outOfKeyRow,
}

export const MelodyRoll: FC<MelodyRollProps> = props => {
  const {
    melody,
    onChange,
    quantized = false,
    playheadMs,
    cursorBeats,
    onCursorChange,
    selectedIndex,
    onSelect,
    compact = false,
    interactive = true,
    className,
  } = props

  const range: RollRange = rollPitchRange(melody)
  const totalBeats = rollBeats(melody)
  const slot = MelodyLib.gridSlotBeats(melody.grid)
  const perBar = MelodyLib.barBeats(melody.tempo.meter)
  const height = rowHeight(range)
  const rows = Array.from({ length: rowCount(range) }, (_, i) => range.high - i)
  const slotCount = Math.round(totalBeats / slot)

  /**
   * The drag in flight. A ref, not state: a pointer move must not re-render on its own, and the
   * melody the drag started from has to survive the re-renders `onChange` causes - otherwise each
   * move would compound against an already-moved note instead of the grab position.
   */
  const drag = useRef<{ drag: MelodyDrag; from: Melody } | null>(null)
  const plot = useRef<SVGSVGElement | null>(null)

  /** Pointer position as a percentage of the plot, or null when there is no laid-out plot yet. */
  const percentOf = (clientX: number, clientY: number): { x: number; y: number } | null => {
    const rect = plot.current?.getBoundingClientRect()
    if (!rect || rect.width === 0 || rect.height === 0) {
      return null
    }
    return { x: ((clientX - rect.left) / rect.width) * 100, y: ((clientY - rect.top) / rect.height) * 100 }
  }

  const handleInsert = (event: MouseEvent<SVGRectElement>) => {
    if (!onChange) {
      return
    }
    const at = percentOf(event.clientX, event.clientY)
    if (!at) {
      return
    }
    const { beat, midi } = pointToCell(at.x, at.y, range, totalBeats, melody.grid)
    onChange(MelodyLib.insertMelodyNote(melody, { midi, onset: beat }))
    /* The cursor follows the note just placed, so keyboard and MIDI entry continue from here. */
    onCursorChange?.(beat + slot)
  }

  const handleGrab = (event: PointerEvent<SVGRectElement>, index: number, kind: MelodyDragKind) => {
    if (!onChange) {
      return
    }
    const at = percentOf(event.clientX, event.clientY)
    if (!at) {
      return
    }
    event.stopPropagation()
    event.currentTarget.setPointerCapture(event.pointerId)
    drag.current = { drag: { kind, index, startXPercent: at.x, startYPercent: at.y }, from: melody }
    onSelect?.(index)
  }

  const handleDragMove = (event: PointerEvent<SVGElement>) => {
    const inFlight = drag.current
    if (!inFlight || !onChange) {
      return
    }
    const at = percentOf(event.clientX, event.clientY)
    if (!at) {
      return
    }
    onChange(
      applyMelodyDrag(
        inFlight.from,
        inFlight.drag,
        at.x - inFlight.drag.startXPercent,
        at.y - inFlight.drag.startYPercent,
        { range, totalBeats }
      )
    )
  }

  const handleDragEnd = () => {
    drag.current = null
  }

  const handleNoteKeyDown = (event: KeyboardEvent<SVGGElement>, index: number) => {
    if (!onChange) {
      return
    }
    const note = melody.notes[index]
    if (!note) {
      return
    }
    if (event.key === 'Delete' || event.key === 'Backspace') {
      event.preventDefault()
      onChange(MelodyLib.removeMelodyNote(melody, index))
      return
    }
    if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') {
      event.preventDefault()
      const step = event.key === 'ArrowRight' ? slot : -slot
      onChange(
        event.shiftKey
          ? MelodyLib.resizeMelodyNote(melody, index, note.duration + step)
          : MelodyLib.moveMelodyNote(melody, index, { midi: note.midi, onset: note.onset + step })
      )
      return
    }
    if (event.key === 'ArrowUp' || event.key === 'ArrowDown') {
      event.preventDefault()
      const step = event.key === 'ArrowUp' ? 1 : -1
      onChange(MelodyLib.moveMelodyNote(melody, index, { midi: note.midi + step, onset: note.onset }))
    }
  }

  const playheadBeats = playheadMs == null ? null : playheadMs / MelodyLib.beatMs(melody.tempo.bpm)

  return (
    <div className={className ? `${styles.root} ${className}` : styles.root}>
      <svg
        ref={plot}
        className={compact ? `${styles.svg} ${styles.compact}` : styles.svg}
        viewBox="0 0 100 100"
        preserveAspectRatio="none"
        role="group"
        aria-label={`Melody piano roll, ${melody.tempo.bpm} bpm, ${melody.tempo.meter.top}/${melody.tempo.meter.bottom}`}
        onPointerMove={handleDragMove}
        onPointerUp={handleDragEnd}
        onPointerCancel={handleDragEnd}
      >
        {rows.map(midi => {
          const kind = rowKind(midi, melody)
          return (
            <rect
              key={`row-${midi}`}
              data-testid="melody-row"
              data-row={kind}
              data-midi={midi}
              className={ROW_CLASS[kind]}
              x={0}
              y={rowY(midi, range)}
              width={100}
              height={height}
            />
          )
        })}

        {Array.from({ length: slotCount + 1 }, (_, i) => {
          const beats = i * slot
          const isBar = Math.abs(beats % perBar) <= MelodyLib.GRID_TOLERANCE
          return (
            <line
              key={`slot-${i}`}
              data-testid={isBar ? 'melody-bar-line' : 'melody-slot-line'}
              className={isBar ? styles.barLine : styles.slotLine}
              x1={beatX(beats, totalBeats)}
              y1={0}
              x2={beatX(beats, totalBeats)}
              y2={100}
            />
          )
        })}

        {onChange ? (
          <rect
            data-testid="melody-insert-surface"
            className={styles.insertSurface}
            x={0}
            y={0}
            width={100}
            height={100}
            onClick={handleInsert}
          />
        ) : null}

        {melody.notes.map((note, index) => {
          const x = beatX(drawnOnset(note, melody, quantized), totalBeats)
          const width = beatX(note.duration, totalBeats)
          const y = rowY(note.midi, range) + (height * (1 - BAR_FILL)) / 2
          const barHeight = height * BAR_FILL
          const selected = selectedIndex === index
          return (
            <g
              key={`${note.onset}-${note.midi}-${index}`}
              className={styles.noteGroup}
              {...(interactive
                ? {
                    role: 'button',
                    tabIndex: 0,
                    'aria-label': MelodyLib.describeMelodyNote(note, melody),
                    onClick: () => onSelect?.(index),
                    onKeyDown: (event: KeyboardEvent<SVGGElement>) => handleNoteKeyDown(event, index),
                  }
                : {})}
            >
              <rect
                data-testid="melody-note"
                className={selected ? `${styles.note} ${styles.noteSelected}` : styles.note}
                x={x}
                y={y}
                width={width}
                height={barHeight}
                opacity={noteOpacity(note.velocity)}
                onPointerDown={event => handleGrab(event, index, 'move')}
              />
              {onChange ? (
                <rect
                  data-testid="melody-note-offset"
                  className={styles.offsetHandle}
                  x={x}
                  y={y}
                  width={Math.min(0.8, width / 4)}
                  height={barHeight}
                  onPointerDown={event => handleGrab(event, index, 'offset')}
                />
              ) : null}
              {onChange ? (
                <rect
                  data-testid="melody-note-resize"
                  className={styles.resizeHandle}
                  x={x + width - Math.min(0.8, width / 4)}
                  y={y}
                  width={Math.min(0.8, width / 4)}
                  height={barHeight}
                  onPointerDown={event => handleGrab(event, index, 'resize')}
                />
              ) : null}
            </g>
          )
        })}

        {playheadBeats == null ? null : (
          <line
            data-testid="melody-playhead"
            className={styles.playhead}
            x1={beatX(playheadBeats, totalBeats)}
            y1={0}
            x2={beatX(playheadBeats, totalBeats)}
            y2={100}
          />
        )}

        {cursorBeats == null || !onChange ? null : (
          <line
            data-testid="melody-cursor"
            className={styles.cursor}
            x1={beatX(cursorBeats, totalBeats)}
            y1={0}
            x2={beatX(cursorBeats, totalBeats)}
            y2={100}
          />
        )}
      </svg>
    </div>
  )
}
